import { inject, ref, watch, type Ref } from 'vue'
import { EventNameEnum, type AIChatSessionMessage } from '@/types/generated'
import { useMetrics } from '@/composables/useMetrics'

interface UseVerdictHintOptions {
  message: Ref<AIChatSessionMessage>
  isLatestMessage: Ref<boolean | undefined>
  chatType: Ref<string>
}

export function useVerdictHint({ message, isLatestMessage, chatType }: UseVerdictHintOptions) {
  const { trackEvent } = useMetrics()
  const courtDbEnabled = inject<Ref<boolean>>('courtDbEnabled', ref(false))
  const setCourtDbEnabled = inject<(value: boolean) => Promise<void>>(
    'setCourtDbEnabled',
    async (value: boolean) => {
      courtDbEnabled.value = value
    }
  )
  const reaskWithCourtDb = inject<() => Promise<void>>('reaskWithCourtDb', async () => {})
  const verdictHintShown = inject<Ref<boolean>>('verdictHintShown', ref(false))
  const markVerdictHintShown = inject<() => void>('markVerdictHintShown', () => {})

  // Local visibility for this instance — stays true once the hint has been shown,
  // so the current hint remains visible after markVerdictHintShown() is called.
  const verdictHintVisible = ref(false)

  watch(
    () =>
      message.value.verdict_activation_hint &&
      isLatestMessage.value &&
      !verdictHintShown.value &&
      !courtDbEnabled.value,
    (shouldShow) => {
      if (shouldShow) {
        verdictHintVisible.value = true
        markVerdictHintShown()
      }
    },
    { immediate: true }
  )

  watch(verdictHintVisible, (visible) => {
    if (visible) {
      void trackEvent(EventNameEnum.VERDICT_ACTIVATION_HINT_SHOWN, {
        message_uuid: message.value.uuid,
        session_type: chatType.value
      })
    }
  })

  const enableCourtDb = () => {
    void setCourtDbEnabled(true)
  }

  const handleReask = () => {
    void reaskWithCourtDb()
  }

  return { verdictHintVisible, enableCourtDb, handleReask }
}
